import { TransformedData } from '@west-australian-newspapers/election-api-types'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import React, { PropsWithChildren, useCallback, useState } from 'react'
import {
    AllEvents,
    DataLayerEventName,
    Election,
    isFederalElection,
    useQueryParams,
} from '@news-mono/web-common'
import { ElectionDataFetchError, ElectionFilter } from '../components'
import {
    filterPartiesByState,
    getPartyColors,
    InterknowlogyData,
    InterknowlogyExtendedParty,
    seatFilterItems,
    SeatFilterOptions,
    transformAreaData,
} from '../data'
import {
    ElectionTppEstCardLoadingPlaceholder,
    PartyTotalsContainer,
    PartyTotalsDescriptionContainer,
    PartyTotalsHeadingContainer,
    PartyTotalsTable,
    PartyTotalsTableData,
    PartyTotalsWidgetContainer,
    TableDataLoadingPlaceholder,
    TableHeadingLoadingPlaceholder,
} from './PartyTotals.styled'
import { format } from 'date-fns'
import { useImpressionAvailable } from '../../../__helpers/impression-available-helper'
import { ElectionTppEstCard } from '../ElectionTppEstCard/ElectionTppEstCard'
import { CustomPageSharing } from '../../../buttons/SharingCustomPage/CustomPageSharing'
import { TheSeatsDivider } from '../TheSeatsWidget/TheSeatsWidget.styled'

type PartyTotalsProps = {
    data: MaybeLoaded<TransformedData>
    onEvent: (event: AllEvents) => void
    election: Election
    description: string
}

export const PartyTotalsComponent = ({
    data,
    onEvent,
    election,
    description,
}: PartyTotalsProps) => {
    const { queryParams, pathname } = useQueryParams()
    const [filter, setFilter] = useState(seatFilterItems[0])

    if (!data.loaded) {
        return (
            <PartyTotalsWrapper
                headingComponent={<TableHeadingLoadingPlaceholder />}
                beforeHeadingComponent={
                    <ElectionTppEstCardLoadingPlaceholder />
                }
            >
                <PartyTotalsTable>
                    {getTableHead()}
                    <tbody>
                        {[...Array(3)].map((_, i) => (
                            <TableDataLoadingPlaceholder />
                        ))}
                    </tbody>
                </PartyTotalsTable>
            </PartyTotalsWrapper>
        )
    }

    if (!data.result) {
        return (
            <PartyTotalsWrapper>
                <ElectionDataFetchError />
            </PartyTotalsWrapper>
        )
    }

    const initialData = transformAreaData(data.result as InterknowlogyData)
    const partyTotals = getPartyData(initialData.areas[0].parties, filter)

    return (
        <PartyTotalsWidgetContainer>
            <PartyTotalsDescriptionContainer>
                <p>{description}</p>
                <CustomPageSharing
                    text={description}
                    shareOptions={[
                        'facebook',
                        'linkedin',
                        'twitter',
                        'email',
                        'clipboard',
                    ]}
                    loading={false}
                    onEvent={onEvent}
                />
            </PartyTotalsDescriptionContainer>

            {isFederalElection(election) && (
                <ElectionFilter
                    headingText="State"
                    queryFilterParam="state"
                    dataLayerEventOriginator="ElectionStateFilter"
                    filterItems={seatFilterItems}
                    onEvent={onEvent}
                    dataLayerEventName={DataLayerEventName.searchFilter}
                    queryParams={queryParams}
                    pathname={pathname}
                    defaultParamName={seatFilterItems[0]}
                    selectedItem={filter}
                    setSelectedItem={setFilter}
                />
            )}
            <TheSeatsDivider hideOnDesktop />
            <ElectionTppEstCard
                title="Two-party preferred estimate"
                data={partyTotals}
            />
            <PartyTotalsWrapper
                headingComponent={
                    <p>
                        {initialData.areas[0].pctCounted.toFixed(0)}% votes
                        counted - updated{' '}
                        {format(new Date(initialData.lastModified), 'h:mm a')}
                    </p>
                }
                onEvent={onEvent}
            >
                <PartyTotalsTable>
                    {getTableHead()}
                    <tbody>
                        {partyTotals.map((party, i) => {
                            // To fill the graph, a party would have to have the maximum number of seats for the election
                            // i.e. so for the bar to be fill a party would need 100% of the seats (this is how ABC did it 🙈)
                            const seatsAsPercent =
                                (party.seatsWon / election.seatsTotal) * 100

                            // We don't have the seats predicted data, so we can't calculate this yet
                            // Calculate the predicted percent, which is the current seats won + the seats ahead
                            // const seatsPredicted = party.seatsWon + party.seatsAhead
                            // const seatsPredictedAsPercent = percentPerSeat * seatsPredicted
                            const seatsPredictedAsPercent = seatsAsPercent // By setting this to the same as seatsAsPercent, it essentially shows nothing as its the same width as the other bar

                            return (
                                <PartyTotalsTableData
                                    key={i}
                                    colors={getPartyColors(party.partyCode)}
                                    seatPercent={Math.min(seatsAsPercent, 100)}
                                    predictedPercent={Math.min(
                                        seatsPredictedAsPercent,
                                        100,
                                    )}
                                >
                                    <td className="partyName">
                                        {party.shortPartyName ||
                                            party.partyName}
                                    </td>
                                    <td className="seatsWon">
                                        {party.seatsWon}
                                        {/* {party.seatsWon} (+{party.seatsAhead}) */}
                                    </td>
                                    <td className="seatsChanged">
                                        {party.seatsChanged}
                                    </td>
                                    <td className="vote">
                                        <div>{party.votePct.toFixed(1)}%</div>
                                        <div className="voteCount">
                                            {/* Intl.NumberFormat adds the commas as per the design */}
                                            {Intl.NumberFormat('en-AU').format(
                                                party.voteCount,
                                            )}
                                        </div>
                                    </td>
                                    <td className="swing">
                                        {/* If the Swing Pct is null, fallback to a dash, was decided previously iirc */}
                                        {party.swingPct
                                            ? `${party.swingPct.toFixed(1)}%`
                                            : '-'}
                                    </td>
                                </PartyTotalsTableData>
                            )
                        })}
                    </tbody>
                </PartyTotalsTable>
            </PartyTotalsWrapper>
        </PartyTotalsWidgetContainer>
    )
}

type PartyTotalsWrapperProps = {
    headingComponent?: React.ReactNode
    beforeHeadingComponent?: React.ReactNode
    onEvent?: (event: AllEvents) => void
}
const PartyTotalsWrapper = ({
    children,
    headingComponent,
    beforeHeadingComponent,
    onEvent,
}: PropsWithChildren<PartyTotalsWrapperProps>) => {
    const impressionAvailableRef = useImpressionAvailable({
        loading: false,
        available: useCallback(() => {
            onEvent?.({
                type: DataLayerEventName.navAvailable,
                originator: 'ElectionPartyTotalsComponent',
                payload: {
                    navName: 'ElectionPartyTotals',
                },
            })
        }, [onEvent]),
    })

    return (
        <PartyTotalsContainer ref={impressionAvailableRef}>
            {beforeHeadingComponent}
            <PartyTotalsHeadingContainer>
                {/* <h2>Seats won & seats likely</h2> */}
                <h2>Seats won</h2>
                {headingComponent}
            </PartyTotalsHeadingContainer>
            {children}
        </PartyTotalsContainer>
    )
}

/** This functions removes all parties that are a part of the coalition but retains the overarching Coalition party added in `transformAreaData` (`getInterknowlogyData.ts`)
 * It also sorts the parties by vote percentage (highest first) and has ability to filter by state
 */
const getPartyData = (
    parties: InterknowlogyExtendedParty[],
    filter: SeatFilterOptions[number],
) => {
    // Grab the "Coalition" party if it exists (it should do)
    const coalition = parties.find((party) => party.partyName === 'Coalition')
    // Filter out any parties that are part of the coalition
    const nonCoalitionParties = parties.filter((party) => !party.isCoalition)
    // Add the coalition party back with the rest of the parties
    const combinedParties = coalition
        ? [coalition, ...nonCoalitionParties]
        : nonCoalitionParties
    // Sort the parties by vote percentage
    const sortedParties = combinedParties.sort((a, b) => b.votePct - a.votePct)
    // If the filter is 'all', return all parties, otherwise filter by the state
    return filterPartiesByState(sortedParties, filter)
}

const getTableHead = () => (
    <thead>
        <tr>
            <th className="partyName"></th>
            <th className="seatsWon">Seats won</th>
            {/* <th className="seatsWon">Seats won (+ likely)</th> */}
            <th className="seatsChanged">Seats changed</th>
            <th className="vote">Vote</th>
            <th className="swing">Swing</th>
        </tr>
    </thead>
)
